// @ts-check
// Taylor's Boats — VDP hero. Thumbnail-to-hero swap, thumbnail carousel
// (prev/next paging over a scroll-snap row) and per-unit walkthrough
// (uploaded video plays inline natively; external URL opens lite-yt poster → iframe).

class VdpHero extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLElement | null} */
    this.heroFigure = this.querySelector('[data-vh-hero]');
    /** @type {HTMLImageElement | null} */
    this.heroImg = this.heroFigure ? this.heroFigure.querySelector('img') : null;
    /** @type {HTMLElement[]} */
    this.thumbs = Array.from(this.querySelectorAll('[data-vh-thumb]'));
    /** @type {HTMLElement | null} */
    this.walkBtn = this.querySelector('[data-vh-walkthrough-btn]');
    /** @type {HTMLElement | null} */
    this.thumbsEl = this.querySelector('[data-vh-thumbs]');
    /** @type {HTMLButtonElement | null} */
    this.prevBtn = this.querySelector('[data-vh-thumbs-prev]');
    /** @type {HTMLButtonElement | null} */
    this.nextBtn = this.querySelector('[data-vh-thumbs-next]');

    /** @type {number} Index of the photo currently shown in the hero. */
    this.activeIndex = 0;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.activateThumb(thumb));
    });

    /** @type {HTMLElement[]} 360° tour thumbs — open an iframe in the hero (no photo). */
    this.tourThumbs = Array.from(this.querySelectorAll('[data-vh-tour]'));
    /** @type {HTMLIFrameElement | null} The on-demand tour iframe (created on first click). */
    this.tourFrame = null;
    this.tourThumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.activateTour(thumb));
    });

    // When the 360 tour is the default-selected element (is-active set in
    // Liquid because the boat has a tour), show it in the hero on load so the
    // selected thumb matches what's displayed. Only boats with a tour pay this.
    const defaultTour = this.tourThumbs.find((t) => t.classList.contains('is-active'));
    if (defaultTour) this.activateTour(defaultTour);

    this.setupLightbox();

    if (this.walkBtn) {
      this.walkBtn.addEventListener('click', () => this.playWalkthrough());
    }

    if (this.thumbsEl) {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollThumbs(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollThumbs(1));
      this.thumbsEl.addEventListener('scroll', () => this.updateNav(), { passive: true });
      window.addEventListener('resize', () => this.updateNav(), { passive: true });
      this.updateNav();
    }
  }

  /** @param {number} dir 1 = forward, -1 = back */
  scrollThumbs(dir) {
    if (!this.thumbsEl) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.thumbsEl.scrollBy({
      left: dir * this.thumbsEl.clientWidth,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  updateNav() {
    if (!this.thumbsEl) return;
    const maxScroll = this.thumbsEl.scrollWidth - this.thumbsEl.clientWidth;
    if (this.prevBtn) this.prevBtn.disabled = this.thumbsEl.scrollLeft <= 1;
    if (this.nextBtn) this.nextBtn.disabled = this.thumbsEl.scrollLeft >= maxScroll - 1;
  }

  /** @param {HTMLElement} thumb */
  activateThumb(thumb) {
    if (!this.heroImg) return;
    const thumbImg = /** @type {HTMLImageElement | null} */ (thumb.querySelector('img'));
    if (!thumbImg) return;

    // Switching back to a photo tears down the 360 tour (stops it loading/CPU).
    this.closeTour();

    this.activeIndex = Number(thumb.dataset.vhIndex) || 0;

    // Swap srcset/src to the higher-resolution variant. The thumb's preview_image
    // is the same media as the hero — Shopify just sized it differently.
    // Build hero src from the thumb's currentSrc by upscaling its width param.
    const thumbSrc = thumbImg.currentSrc || thumbImg.src;
    const heroSrc = thumbSrc.replace(/(\?|&)width=\d+/, '$1width=1600');

    this.heroImg.removeAttribute('srcset');
    this.heroImg.removeAttribute('sizes');
    this.heroImg.src = heroSrc;
    this.heroImg.alt = thumbImg.alt || '';

    this.thumbs.forEach((t) => {
      const isActive = t === thumb;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    this.tourThumbs.forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-pressed', 'false');
    });

    // Keep the active thumb fully visible inside the carousel row.
    thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /**
   * Show the 360° virtual tour inline in the hero viewer. The iframe is created
   * on first click (no load cost until the visitor opens the tour).
   * @param {HTMLElement} thumb
   */
  activateTour(thumb) {
    if (!this.heroFigure) return;
    const url = thumb.dataset.vhTour;
    if (!url) return;

    if (!this.tourFrame) {
      const iframe = document.createElement('iframe');
      iframe.src = url;
      iframe.className = 'vh-hero__tour';
      iframe.allow = 'accelerometer; autoplay; gyroscope; fullscreen; xr-spatial-tracking';
      iframe.setAttribute('allowfullscreen', '');
      iframe.title = '360° virtual tour';
      this.heroFigure.appendChild(iframe);
      this.tourFrame = iframe;
    }
    this.heroFigure.classList.add('vh-hero--tour-active');

    this.thumbs.forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-pressed', 'false');
    });
    this.tourThumbs.forEach((t) => {
      const isActive = t === thumb;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /** Remove the tour iframe and restore the photo view. */
  closeTour() {
    if (this.tourFrame) {
      this.tourFrame.remove();
      this.tourFrame = null;
    }
    if (this.heroFigure) this.heroFigure.classList.remove('vh-hero--tour-active');
  }

  playWalkthrough() {
    if (!this.walkBtn || this.walkBtn.dataset.playing === 'true') return;
    const embedUrl = this.walkBtn.dataset.embedUrl;
    if (!embedUrl) return;

    const iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.allow =
      'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
    iframe.setAttribute('allowfullscreen', '');
    iframe.title = this.walkBtn.getAttribute('aria-label') || 'Walkthrough video';
    this.walkBtn.appendChild(iframe);
    this.walkBtn.dataset.playing = 'true';
  }

  // ===== Full-screen photo lightbox =====

  setupLightbox() {
    /** @type {HTMLDialogElement | null} */
    this.lightbox = this.querySelector('[data-vh-lightbox]');
    if (!this.lightbox) return;

    const dataEl = this.querySelector('[data-vh-lightbox-data]');
    /** @type {{ src: string, alt: string }[]} */
    this.slides = [];
    try {
      this.slides = dataEl ? JSON.parse(dataEl.textContent || '[]') : [];
    } catch (e) {
      this.slides = [];
    }
    if (!this.slides.length) return;

    /** @type {number} */
    this.lbIndex = 0;
    /** @type {HTMLImageElement | null} */
    this.lbImg = this.lightbox.querySelector('[data-vh-lightbox-img]');
    /** @type {HTMLElement | null} */
    this.lbCounter = this.lightbox.querySelector('[data-vh-lightbox-counter]');
    /** @type {HTMLElement | null} */
    this.lbStage = this.lightbox.querySelector('[data-vh-lightbox-stage]');
    /** @type {HTMLButtonElement | null} */
    this.lbPrev = this.lightbox.querySelector('[data-vh-lightbox-prev]');
    /** @type {HTMLButtonElement | null} */
    this.lbNext = this.lightbox.querySelector('[data-vh-lightbox-next]');
    const lbClose = this.lightbox.querySelector('[data-vh-lightbox-close]');
    const openBtn = this.querySelector('[data-vh-zoom-open]');

    // Open from the magnifier button or by clicking the hero image itself.
    if (openBtn) openBtn.addEventListener('click', () => this.openLightbox(this.activeIndex));
    if (this.heroFigure) {
      this.heroFigure.addEventListener('click', (event) => {
        if (event.target instanceof Element && event.target.closest('[data-vh-zoom-open]')) return;
        this.openLightbox(this.activeIndex);
      });
    }

    if (lbClose) lbClose.addEventListener('click', () => this.lightbox?.close());
    if (this.lbPrev) this.lbPrev.addEventListener('click', () => this.stepLightbox(-1));
    if (this.lbNext) this.lbNext.addEventListener('click', () => this.stepLightbox(1));

    // Click the dark stage background (not the image) to dismiss.
    if (this.lbStage) {
      this.lbStage.addEventListener('click', (event) => {
        if (event.target === this.lbStage) this.lightbox?.close();
      });
    }

    // Arrow-key navigation while open (Escape is handled natively by <dialog>).
    this.lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.stepLightbox(1);
      else if (event.key === 'ArrowLeft') this.stepLightbox(-1);
    });

    // Reset the zoom state whenever the dialog closes.
    this.lightbox.addEventListener('close', () => this.resetZoom());

    if (this.lbImg) {
      this.lbImg.addEventListener('click', () => this.toggleZoom());
      this.lbImg.addEventListener('pointermove', (event) => this.panZoom(event));
    }
  }

  /** @param {number} index */
  openLightbox(index) {
    if (!this.lightbox || !this.slides.length || this.lightbox.open) return;
    this.lbIndex = Math.min(Math.max(index, 0), this.slides.length - 1);
    this.renderLightbox();
    this.lightbox.showModal();
  }

  /** @param {number} dir 1 = next, -1 = previous */
  stepLightbox(dir) {
    if (!this.slides.length) return;
    const count = this.slides.length;
    this.lbIndex = (this.lbIndex + dir + count) % count;
    this.resetZoom();
    this.renderLightbox();
  }

  renderLightbox() {
    const slide = this.slides[this.lbIndex];
    if (!slide || !this.lbImg) return;
    this.lbImg.src = slide.src;
    this.lbImg.alt = slide.alt || '';

    const multiple = this.slides.length > 1;
    if (this.lbCounter) {
      this.lbCounter.textContent = multiple ? `${this.lbIndex + 1} / ${this.slides.length}` : '';
    }
    if (this.lbPrev) this.lbPrev.hidden = !multiple;
    if (this.lbNext) this.lbNext.hidden = !multiple;

    // Warm the neighbours so prev/next feel instant.
    [this.lbIndex + 1, this.lbIndex - 1].forEach((i) => {
      const neighbour = this.slides[(i + this.slides.length) % this.slides.length];
      if (neighbour) new Image().src = neighbour.src;
    });
  }

  toggleZoom() {
    this.lbImg?.classList.toggle('is-zoomed');
  }

  /** @param {PointerEvent} event */
  panZoom(event) {
    if (!this.lbImg || !this.lbImg.classList.contains('is-zoomed')) return;
    const rect = this.lbImg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    this.lbImg.style.transformOrigin = `${x}% ${y}%`;
  }

  resetZoom() {
    if (!this.lbImg) return;
    this.lbImg.classList.remove('is-zoomed');
    this.lbImg.style.transformOrigin = '';
  }
}

if (!customElements.get('vdp-hero')) {
  customElements.define('vdp-hero', VdpHero);
}
